import { Controller } from "@hotwired/stimulus";

/**
 * Response Modal Controller
 *
 * Manages the modal for displaying LLM response content
 */
export default class extends Controller {
  connect() {
    // Find modal elements
    this.modal = document.getElementById("response-modal");
    this.modalTitle = document.getElementById("response-modal-title");
    this.modalContent = document.getElementById("response-modal-content");
    this.modalMetadata = document.getElementById("response-modal-metadata");
  }

  /**
   * Opens the modal and displays the response
   * @param {Event} event - The triggering event
   */
  open(event) {
    event.preventDefault();

    const responseData = JSON.parse(
      event.currentTarget.dataset.responseData || "{}",
    );

    if (!this.modal) {
      console.error("Response modal not found");
      return;
    }

    // Set modal title
    if (this.modalTitle) {
      this.modalTitle.textContent = `Response from ${responseData.model_used || "Unknown Model"}`;
    }

    // Set metadata
    if (this.modalMetadata) {
      const metadataHTML = this.buildMetadataHTML(responseData);
      this.modalMetadata.innerHTML = metadataHTML;
    }

    // Render response content
    if (this.modalContent) {
      this.renderResponse(responseData);
    }

    // Open modal
    this.modal.showModal();
  }

  /**
   * Closes the modal
   */
  close() {
    if (this.modal) {
      this.modal.close();
    }
  }

  /**
   * Renders the response content
   */
  renderResponse(responseData) {
    if (!this.modalContent) return;

    let html = "";

    // Response content (markdown is already rendered on server)
    if (responseData.content_html) {
      html += `
        <div class="prose prose-sm max-w-none mb-6">
          ${responseData.content_html}
        </div>
      `;
    } else if (responseData.content) {
      // Fallback: escape and display as plain text
      html += `
        <div class="prose prose-sm max-w-none mb-6">
          <p>${this.escapeHtml(responseData.content)}</p>
        </div>
      `;
    }

    // Brand mentions section
    if (responseData.brand_names && responseData.brand_names.length > 0) {
      html += `
        <div class="divider"></div>
        <div class="mb-4">
          <h4 class="font-semibold text-sm mb-2">Brands Mentioned (${responseData.brand_names.length})</h4>
          <div class="flex flex-wrap gap-2">
            ${responseData.brand_names
              .map((brandName) => {
                return `<div class="badge badge-primary badge-sm">${this.escapeHtml(brandName)}</div>`;
              })
              .join("")}
          </div>
        </div>
      `;
    }

    // Citations
    if (responseData.citations && responseData.citations.length > 0) {
      html += `
        <div class="divider"></div>
        <div class="mb-4">
          <h4 class="font-semibold text-sm mb-2">Citations (${responseData.citations.length})</h4>
          <ul class="list-disc list-inside space-y-1 text-sm">
            ${responseData.citations
              .map((citation) => {
                let citationHtml = "";
                if (citation.title) {
                  citationHtml += `<strong>${this.escapeHtml(citation.title)}</strong>`;
                }
                if (citation.url) {
                  const escapedUrl = this.escapeHtml(citation.url);
                  citationHtml += ` <a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" class="link link-primary">${escapedUrl}</a>`;
                }
                return `<li>${citationHtml}</li>`;
              })
              .join("")}
          </ul>
        </div>
      `;
    }

    this.modalContent.innerHTML = html;
  }

  /**
   * Builds metadata HTML
   */
  buildMetadataHTML(responseData) {
    const parts = [];

    if (responseData.model_used) {
      parts.push(
        `<span class="badge badge-outline">${this.escapeHtml(responseData.model_used)}</span>`,
      );
    }

    if (responseData.queried_at || responseData.created_at) {
      const date = new Date(responseData.queried_at || responseData.created_at);
      parts.push(
        `<span class="text-sm text-base-content/60">${date.toLocaleString()}</span>`,
      );
    }

    if (responseData.analysis_job_id) {
      parts.push(
        `<span class="text-sm text-base-content/60">Analysis #${responseData.analysis_job_id}</span>`,
      );
    }

    return parts.join(" • ");
  }

  /**
   * Escapes HTML to prevent XSS
   */
  escapeHtml(text) {
    if (text == null) return "";
    const div = document.createElement("div");
    div.textContent = String(text);
    return div.innerHTML;
  }

}
